import { Theme } from '@emotion/react';
import { formatPhoneNumberIntl } from 'react-phone-number-input/input-core';

import { AddressSuggestion, Recipient } from 'api/api';

import metadata from '~metadata/libphonenumber-metadata.custom.json';

import { formatApartmentNumber } from '../config/apartment-number-config';
import {
  AddressFormatOptions,
  AddressToken,
  StructuredAddressToken,
  formatAddressToArray,
  formatAddressToTokens,
} from './format';

type SensitiveDataType = 'email' | 'phone_number' | 'national_id_number';

export function formatSuggestionToHTML(suggestion: AddressSuggestion, country: string) {
  const invalid_components = suggestion.invalid_components || [];

  const options: AddressFormatOptions = {
    country,
    show_street: true,
    show_city: true,
    show_postal_code: true,
    show_region: true,
    presentational: true,
  };

  const tokens = formatAddressToTokens(suggestion.address!, options);

  return tokens.map(token => {
    if (typeof token === 'string') {
      return token;
    }
    if (
      invalid_components.includes(token.name.toUpperCase()) ||
      (invalid_components.includes('ADDRESS_LINES') && token.name === 'street') // in address line only input we won't recieve street as invalid component
    ) {
      return <b key={token.name}>{token.value}</b>;
    }
    return <span key={token.name}>{token.value}</span>;
  });
}

const mapRegionTokenToExtendedVersion = (
  token: StructuredAddressToken,
  regions?: Record<string, string>
) => {
  if (token.name === 'region_code') {
    const extendedRegionName = regions?.[token.value]
      ? `${regions[token.value]} (${token.value})`
      : token.value;

    return {
      name: 'region',
      value: extendedRegionName,
    };
  } else {
    return token;
  }
};

const formatDiffLayout = (
  tokens: AddressToken[],
  invalidComponents: string[],
  color: string,
  regions?: Record<string, string>
) =>
  tokens.map((token, i) => {
    if (typeof token === 'string') {
      if (token === '\n') {
        return <br key={i} />;
      }
      return token;
    }

    const normalisedToken = mapRegionTokenToExtendedVersion(token, regions);

    if (invalidComponents.includes(normalisedToken.name.toUpperCase())) {
      return (
        <span key={normalisedToken.name} css={{ color }}>
          [{normalisedToken.value}]
        </span>
      );
    }

    return <span key={normalisedToken.name}>{normalisedToken.value}</span>;
  });

export function formatAddressDiff(
  theme: Theme,
  address: Recipient,
  suggestion: AddressSuggestion,
  country: string,
  regions?: Record<string, string>
) {
  const invalid_components = suggestion.invalid_components || [];

  const options: AddressFormatOptions = {
    country,
    multiline: true,
    show_street: true,
    show_city: true,
    show_postal_code: true,
    show_region: true,
    presentational: true,
  };

  const addressTokens = formatAddressToTokens(address, options);
  const suggestionTokens = formatAddressToTokens(suggestion.address!, options);

  return [
    formatDiffLayout(addressTokens, invalid_components, theme.darkOrange, regions),
    formatDiffLayout(suggestionTokens, invalid_components, theme.green, regions),
  ];
}

type RecipientFormatOptions = {
  omitContactInfo?: boolean;
};

export function formatRecipientToArray(
  address: Recipient,
  country: string,
  options?: RecipientFormatOptions
) {
  const addressLines = formatAddressToArray(formatApartmentNumber(address, country), {
    country,
    multiline: true,
    show_street: true,
    show_city: true,
    show_postal_code: true,
    show_region: true,
    presentational: true,
  });

  const valueMasking = (value: string) => {
    const valueLength = value.length;
    const middleDotSymbol = String.fromCharCode(8226);

    if (valueLength <= 2) return value;

    if (valueLength <= 5) {
      const firstCharacter = value[0];
      const lastCharacter = value.slice(-1);
      return `${firstCharacter}${middleDotSymbol.repeat(valueLength - 2)}${lastCharacter}`;
    }

    const firstTwoCharacters = `${value[0]}${value[1]}`;
    const lastTwoCharacters = value.slice(-2);
    return `${firstTwoCharacters}${middleDotSymbol.repeat(valueLength - 4)}${lastTwoCharacters}`;
  };

  const maskSensitiveInformation = (type: SensitiveDataType) => {
    switch (type) {
      case 'email': {
        if (!address.email) return [];
        const email = address.email.split('@')[0];
        const emailDomain = address.email.split('@')[1];

        return `${valueMasking(email)}@${emailDomain}`;
      }
      case 'national_id_number':
        if (!address.national_id_number) return [];
        return valueMasking(address.national_id_number);
      case 'phone_number':
        if (!address.phone_number) return [];

        const splittedPhoneNumber = address.phone_number.split(/\s/g);
        const phoneNumberPrefix = splittedPhoneNumber[0].slice(0).split('+')[1];
        const phoneNumberWithoutPrefix = splittedPhoneNumber.slice(1).join('');

        return `+${phoneNumberPrefix} ${valueMasking(phoneNumberWithoutPrefix)}`;
    }
  };

  if (options?.omitContactInfo) {
    return [
      ...(address.company_name ? [address.company_name] : []),
      ...(address.first_name && address.last_name
        ? [`${address.first_name}  ${address.last_name}`]
        : []),
      ...(address.name ? [address.name] : []),
      ...addressLines,
    ];
  }

  return [
    ...(address.company_name ? [address.company_name] : []),
    ...(address.first_name && address.last_name
      ? [`${address.first_name}  ${address.last_name}`]
      : []),
    ...(address.care_of ? [address.care_of] : []),
    ...(address.name ? [address.name] : []),
    ...addressLines,
    ...(address.floor_number ? [address.floor_number] : []),
    [maskSensitiveInformation('national_id_number')],
    [maskSensitiveInformation('phone_number')],
    [maskSensitiveInformation('email')],
    ...(address.vat ? [address.vat] : []),
  ];
}

export function applyPhoneNumberMask(address: Recipient) {
  if (!address.phone_number) {
    return address;
  }

  return {
    ...address,
    phone_number: formatPhoneNumberIntl(address.phone_number, metadata),
  };
}
